import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import './Login.css';

/* ─── Forgot Password Modal ─────────────────────── */
function ForgotPasswordModal({ onClose, initialEmail }) {
  const [email, setEmail] = useState(initialEmail || '');
  const [fieldError, setFieldError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  // Close on Escape key
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function validate() {
    if (!email.trim()) return 'El correo electrónico es requerido.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return 'Ingresa un correo electrónico válido.';
    if (!email.trim().toLowerCase().endsWith('@wsp.com.mx')) return 'Solo se permiten correos con dominio @wsp.com.mx.';
    return '';
  }

  function handleEmailChange(e) {
    setEmail(e.target.value);
    if (fieldError) setFieldError('');
    if (submitError) setSubmitError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const validationErr = validate();
    if (validationErr) {
      setFieldError(validationErr);
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);

    if (error) {
      setSubmitError(
        error.status === 429
          ? 'Supabase ha alcanzado el límite de correos permitidos por hora. Espera unos minutos e intenta de nuevo.'
          : 'No se pudo enviar el correo. Intenta de nuevo más tarde.'
      );
      return;
    }

    setSent(true);
  }

  return (
    <>
      <div className="forgot-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="forgot-modal" role="dialog" aria-modal="true" aria-labelledby="forgot-title">

        {!sent ? (
          <>
            <div className="forgot-modal-header">
              <h2 id="forgot-title" className="forgot-modal-title">
                Restablecer contraseña
              </h2>
              <button
                type="button"
                className="forgot-modal-close"
                onClick={onClose}
                aria-label="Cerrar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <p className="forgot-modal-subtitle">
              Si tienes acceso a un administrador del sistema, pídele que restablezca tu contraseña desde la plataforma. Si no es posible, ingresa tu correo corporativo y te enviaremos un enlace.
            </p>

            <form onSubmit={handleSubmit} noValidate className="forgot-form">
              <div className="forgot-form-group">
                <label htmlFor="forgot-email" className="forgot-label">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    className="label-icon">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                  Correo electrónico
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="usuario@wsp.com.mx"
                  className={fieldError ? 'input-error' : ''}
                  autoComplete="email"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
                {fieldError && <span className="error-msg">{fieldError}</span>}
              </div>

              {submitError && (
                <div className="login-error-banner" role="alert">{submitError}</div>
              )}

              <div className="forgot-actions">
                <button
                  type="button"
                  className="forgot-btn-cancel"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="forgot-btn-send"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="forgot-spinner" aria-hidden="true" />
                      Enviando…
                    </>
                  ) : (
                    'Enviar enlace'
                  )}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="forgot-success">
            <div className="forgot-success-icon" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h2 className="forgot-success-title">¡Correo enviado!</h2>
            <p className="forgot-success-body">
              Si <strong>{email.trim()}</strong> está registrado en el sistema, recibirás un enlace para restablecer tu contraseña en los próximos minutos.
            </p>
            <p className="forgot-success-note">
              Revisa también tu carpeta de spam si no lo encuentras.
            </p>
            <button type="button" className="btn-primary" onClick={onClose}>
              Entendido
            </button>
          </div>
        )}

      </div>
    </>
  );
}

/* ─── Login ─────────────────────────────────────── */
function Login() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);

  function validate() {
    const newErrors = {};
    if (!form.email.trim()) {
      newErrors.email = 'El correo electrónico es requerido.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Ingresa un correo electrónico válido.';
    }
    if (!form.password) {
      newErrors.password = 'La contraseña es requerida.';
    } else if (form.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres.';
    }
    return newErrors;
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (loginError) setLoginError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: form.email,
      password: form.password,
    });
    setLoading(false);

    if (error) {
      setLoginError('Correo o contraseña incorrectos.');
    } else {
      navigate('/dashboard');
    }
  }

  function handleForgotPassword() {
    setShowForgotModal(true);
  }

  return (
    <>
    <div className="login-container">
      <div className="login-card">
        <div className="login-logo">
          <img src="/WSP_LOGO-01.jpg" alt="WSP" className="wsp-logo" />
          <span className="login-logo-text">T.R.A.C.K.</span>
          <p className="login-logo-subtitle">Time &amp; Requirements ABAP Control Kardex</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="email">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="label-icon">
                <rect x="2" y="4" width="20" height="16" rx="2"/>
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
              </svg>
              Correo electrónico
            </label>
            <input
              id="email"
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="usuario@wsp.com.mx"
              className={errors.email ? 'input-error' : ''}
              autoComplete="email"
            />
            {errors.email && <span className="error-msg">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="label-icon">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              Contraseña
            </label>
            <div className={`input-wrapper ${errors.password ? 'input-error' : ''}`}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder="••••••••"
                autoComplete="current-password"
              />
              <button
                type="button"
                className="btn-toggle-password"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showPassword ? (
                  // Eye-off icon
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  // Eye icon
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            {errors.password && <span className="error-msg">{errors.password}</span>}
          </div>

          {loginError && <div className="login-error-banner">{loginError}</div>}

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Ingresando...' : 'Iniciar sesión'}
          </button>

          <button
            type="button"
            className="btn-forgot"
            onClick={handleForgotPassword}
          >
            ¿Olvidaste tu contraseña?
          </button>
        </form>
      </div>
    </div>

    {showForgotModal && (
      <ForgotPasswordModal
        onClose={() => setShowForgotModal(false)}
        initialEmail={form.email}
      />
    )}
    </>
  );
}

export default Login;
